<script setup>
import { useReferenceWorkspace } from "./referenceWorkspace";

const {
  reference,
  cancelModalOpen,
  deleteModalOpen,
  deleteInvoice,
  deletingReference,
  toast,
  hasLinkedInvoice,
  linkedInvoiceLabel,
  setVersionCancelled,
  closeCancelModal,
  closeDeleteModal,
  destroyReference,
} = useReferenceWorkspace();
</script>

<template>
  <div
    v-if="deleteModalOpen"
    data-testid="delete-reference-modal"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
    @click.self="closeDeleteModal"
  >
    <div
      class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900"
    >
      <div class="space-y-3">
        <div
          class="text-xs font-semibold uppercase tracking-[0.2em] text-rose-500"
        >
          Delete Reference
        </div>
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Delete {{ reference.data?.name || "this reference" }}?
        </h2>
        <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
          This will remove the reference from the active job list and you will
          be redirected back to jobs.
        </p>
        <label
          v-if="hasLinkedInvoice"
          class="flex items-start gap-3 rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:text-slate-200"
        >
          <input
            v-model="deleteInvoice"
            data-testid="delete-linked-invoice"
            type="checkbox"
            class="mt-1 h-4 w-4 rounded border-slate-300"
          />
          <span>{{ linkedInvoiceLabel }}</span>
        </label>
      </div>

      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <button
          class="btn"
          :disabled="deletingReference"
          @click.prevent="closeDeleteModal"
        >
          Keep Job
        </button>
        <button
          data-testid="confirm-delete-reference"
          class="btn-plain bg-red-700"
          :disabled="deletingReference"
          @click.prevent="destroyReference"
        >
          {{ deletingReference ? "Deleting..." : "Delete Job" }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="cancelModalOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 px-4"
    @click.self="closeCancelModal"
  >
    <div
      class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl dark:bg-slate-900"
    >
      <div class="space-y-3">
        <div
          class="text-xs font-semibold uppercase tracking-[0.2em] text-rose-500"
        >
          Cancel Accepted Job
        </div>
        <h2 class="text-xl font-semibold text-slate-900 dark:text-slate-100">
          Remove this event from the calendar?
        </h2>
        <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
          Cancelling this accepted reference will remove the event from the
          calendar and clear its accepted status.
        </p>
        <p class="text-sm leading-6 text-slate-600 dark:text-slate-300">
          Continue only if you want to cancel the job and remove all calendar
          entries linked to it.
        </p>
      </div>

      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <button class="btn" @click.prevent="closeCancelModal">
          Keep Event
        </button>
        <button
          class="btn-plain bg-red-600"
          @click.prevent="setVersionCancelled"
        >
          Cancel And Remove
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="toast.visible"
      data-testid="reference-toast"
      class="fixed bottom-4 left-4 right-4 z-[9999] flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium shadow-xl sm:bottom-6 sm:left-auto sm:right-6 sm:max-w-xl"
      :class="
        toast.tone === 'error'
          ? 'bg-rose-600 text-white'
          : 'bg-emerald-600 text-white'
      "
      role="status"
      aria-live="polite"
    >
      <svg
        v-if="toast.tone !== 'error'"
        class="h-4 w-4 shrink-0 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        />
      </svg>
      <svg
        v-else
        class="h-4 w-4 shrink-0 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
      <span>{{ toast.message }}</span>
    </div>
  </Teleport>
</template>
